import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  Subject,
} from 'rxjs';
import {
  IReporteBase,
  IUbicacion,
  IRecorrido,
  IRecorridoDinamico,
  IGeoJSONCircle,
  Stop,
  IConfigMarker,
  IEventoGenerico,
} from 'modelos/src';
import { SeleccionadosBarraLateral, MapInstanceFilter } from './interfaces';
import { TipoMapaBase } from '../../../standalone/selector-tipo-mapa/selector-tipo-mapa.component';

/**
 * Servicio centralizado de estado para el módulo mapa-vehiculos.
 *
 * Flujo principal de datos:
 *
 *   contenedor-mapa
 *     → setReportes() / updateSingleReporte()       (lista completa + WS push)
 *     → updateSeleccionadoBarraLateralPorNumero()   (click en vehículo del mapa)
 *
 *   barra-lateral / componentes de búsqueda
 *     → updateSeleccionadoBarraLateral()            (cambios de estado del panel)
 *     ← seleccionadoActual$                         (reaccionan al mapa activo)
 *
 *   mapa-individual
 *     ← seleccionadosBarraLateral$ | async          (binding en template)
 *     ← reportes$ | async                           (binding en template)
 *     ← reporteRealTime$                            (actualizaciones WS)
 */
@Injectable({
  providedIn: 'root',
})
export class MapaVehiculosService {
  constructor() {}

  // ─────────────────────────────────────────────────────────────────────────
  // REPORTES DE VEHÍCULOS
  // ─────────────────────────────────────────────────────────────────────────

  // Lista completa de reportes (un reporte por vehículo, deduplicada).
  // Se carga al iniciar y se refresca por polling.
  private reportesSubject = new BehaviorSubject<IReporteBase<'Tracker 4G'>[]>(
    [],
  );
  public reportes$: Observable<IReporteBase<'Tracker 4G'>[]> =
    this.reportesSubject.asObservable();

  // Canal de actualización en tiempo real (WebSocket push, un reporte a la vez).
  // mapa-individual se suscribe a este en lugar de reportes$ para evitar
  // reconstruir todos los markers ante cada actualización.
  private reporteRealTimeSubject =
    new BehaviorSubject<IReporteBase<'Tracker 4G'> | null>(null);
  public reporteRealTime$: Observable<IReporteBase<'Tracker 4G'> | null> =
    this.reporteRealTimeSubject.asObservable();

  // Canal de actualización en LOTE (batch WS del mapa): emite el array de reportes
  // cambiados en una sola emisión rxjs por batch (en vez de una por vehículo).
  // Evita ~728 emisiones/batch → mucho menos overhead de rxjs/CPU.
  private reporteRealTimeBatchSubject = new Subject<
    IReporteBase<'Tracker 4G'>[]
  >();
  public reporteRealTimeBatch$: Observable<IReporteBase<'Tracker 4G'>[]> =
    this.reporteRealTimeBatchSubject.asObservable();

  // Índice vehicleId (idEntidad + idsAsignados) → posición en el array de reportes.
  // Permite que las actualizaciones WS sean O(1) en vez de findIndex O(n)
  // (con ~728 vehículos por batch eso era O(n²) por batch).
  private reportesIndex = new Map<string, number>();

  // vehicleIds cuyo reporte ya tiene el detalle cargado (los seleccionados).
  // El listado del mapa es liviano (solo lo que dibuja el marker), así que los
  // campos extra que trajo el pedido por id se preservan entre polls.
  private idsConDetalle = new Set<string>();

  // Señal que dispara un nuevo poll de reportes. El timer de contenedor-mapa
  // llama a refrescarReportes() cada X segundos para mantener la lista actualizada.
  private refrescarReportesSubject = new Subject<void>();
  public refrescarReportes$ = this.refrescarReportesSubject.asObservable();

  // ─────────────────────────────────────────────────────────────────────────
  // DATOS DE REFERENCIA (leídos por mapa-individual via bindings del template)
  // ─────────────────────────────────────────────────────────────────────────

  private ubicacionesSubject = new BehaviorSubject<IUbicacion[]>([]);
  public ubicaciones$: Observable<IUbicacion[]> =
    this.ubicacionesSubject.asObservable();

  private recorridosSubject = new BehaviorSubject<IRecorrido[]>([]);
  public recorridos$: Observable<IRecorrido[]> =
    this.recorridosSubject.asObservable();

  private recorridosDinamicosSubject = new BehaviorSubject<
    IRecorridoDinamico[]
  >([]);
  public recorridosDinamicos$: Observable<IRecorridoDinamico[]> =
    this.recorridosDinamicosSubject.asObservable();

  private configMarkersSubject = new BehaviorSubject<IConfigMarker[]>([]);
  public configMarkers$: Observable<IConfigMarker[]> =
    this.configMarkersSubject.asObservable();

  // ─────────────────────────────────────────────────────────────────────────
  // CONFIGURACIÓN DE MAPAS
  // ─────────────────────────────────────────────────────────────────────────

  private cantMapasSubject = new BehaviorSubject<number>(1);
  public cantMapas$: Observable<number> = this.cantMapasSubject.asObservable();

  // Mapa activo (1-based). Controla qué entrada de seleccionadosBarraLateral
  // expone seleccionadoActual$.
  private mapaSeleccionadoSubject = new BehaviorSubject<number>(1);
  public mapaSeleccionado$: Observable<number> =
    this.mapaSeleccionadoSubject.asObservable();

  private tipoMapaBaseSubject = new BehaviorSubject<TipoMapaBase>('osm');
  public tipoMapaBase$: Observable<TipoMapaBase> =
    this.tipoMapaBaseSubject.asObservable();

  // ─────────────────────────────────────────────────────────────────────────
  // ESTADO DE BARRA LATERAL POR MAPA
  // ─────────────────────────────────────────────────────────────────────────

  // Array con el estado completo de cada mapa (uno por instancia activa).
  // Incluye: vehículo seleccionado, resultados de búsquedas, opciones visuales.
  // Es la fuente de verdad principal del panel lateral.
  private seleccionadosBarraLateralSubject = new BehaviorSubject<
    SeleccionadosBarraLateral[]
  >([]);
  public seleccionadosBarraLateral$: Observable<SeleccionadosBarraLateral[]> =
    this.seleccionadosBarraLateralSubject.asObservable();

  // Estado del mapa activo. Los componentes de búsqueda se suscriben a este
  // en lugar de recibir @Input, y llaman updateSeleccionadoBarraLateral() para escribir.
  // Nota: distinctUntilChanged compara por referencia — emite ante cualquier
  // cambio de objeto aunque los valores sean iguales (comportamiento esperado,
  // ya que updateSeleccionadoBarraLateral siempre crea un nuevo objeto con spread).
  public seleccionadoActual$: Observable<
    SeleccionadosBarraLateral | undefined
  > = combineLatest([
    this.seleccionadosBarraLateralSubject,
    this.mapaSeleccionadoSubject,
  ]).pipe(
    map(([seleccionados, mapa]) =>
      seleccionados.find((s) => s.mapaSeleccionado === mapa),
    ),
    distinctUntilChanged(),
  );

  // Espejo de reporteSeleccionado indexado por número de mapa.
  // Existe en paralelo a seleccionadosBarraLateral.reporteSeleccionado para que
  // barra-lateral pueda reaccionar solo a cambios de vehículo seleccionado
  // sin suscribirse al estado completo del panel.
  private reporteSeleccionadoPorMapaSubject = new BehaviorSubject<{
    [mapa: number]: IReporteBase<'Tracker 4G'> | undefined;
  }>({});
  public reporteSeleccionadoPorMapa$: Observable<{
    [mapa: number]: IReporteBase<'Tracker 4G'> | undefined;
  }> = this.reporteSeleccionadoPorMapaSubject.asObservable();

  // ─────────────────────────────────────────────────────────────────────────
  // FILTROS POR MAPA
  // ─────────────────────────────────────────────────────────────────────────

  // Filtros activos (cliente, grupo, función) indexados por número de mapa.
  // También presentes en seleccionadosBarraLateral.filtrosSeleccionados;
  // este subject separado permite que mapa-individual reaccione solo a cambios
  // de filtro sin procesar el estado completo del panel.
  private filtrosPorMapaSubject = new BehaviorSubject<{
    [mapa: number]: MapInstanceFilter | undefined;
  }>({});
  public filtrosPorMapa$: Observable<{
    [mapa: number]: MapInstanceFilter | undefined;
  }> = this.filtrosPorMapaSubject.asObservable();

  // Set de IDs de vehículos visibles para cada mapa (mapIndex → Set<vehicleId>).
  // Cuando existe la key, solo se muestran los vehículos del Set.
  // Cuando no existe, se muestran todos (sin búsqueda activa).
  private vehiculosFiltradosPorMapaSubject = new BehaviorSubject<
    Map<number, Set<string>>
  >(new Map());
  public vehiculosFiltradosPorMapa$: Observable<Map<number, Set<string>>> =
    this.vehiculosFiltradosPorMapaSubject.asObservable();

  // ─────────────────────────────────────────────────────────────────────────
  // ELEMENTOS INTERACTIVOS DE BÚSQUEDA
  // ─────────────────────────────────────────────────────────────────────────

  // Parada o viaje seleccionado en el panel (detenido / recorridos).
  // mapa-individual lo usa para resaltar el marker correspondiente.
  private paradaSeleccionadaSubject = new BehaviorSubject<Stop | undefined>(
    undefined,
  );
  public paradaSeleccionada$: Observable<Stop | undefined> =
    this.paradaSeleccionadaSubject.asObservable();

  // Círculo dibujado en el mapa para la búsqueda por zona.
  // busqueda-zona escribe aquí, mapa-individual lo dibuja.
  private circuloBusquedaSubject = new BehaviorSubject<
    IGeoJSONCircle | undefined
  >(undefined);
  public circuloBusqueda$: Observable<IGeoJSONCircle | undefined> =
    this.circuloBusquedaSubject.asObservable();

  // Polígono de ubicación para búsqueda por zona (modo Ubicación).
  private poligonoBusquedaSubject = new BehaviorSubject<IUbicacion | undefined>(
    undefined,
  );
  public poligonoBusqueda$: Observable<IUbicacion | undefined> =
    this.poligonoBusquedaSubject.asObservable();

  // Evento seleccionado en el panel de eventos.
  private eventoSeleccionadoSubject = new BehaviorSubject<
    IEventoGenerico | undefined
  >(undefined);
  public eventoSeleccionado$: Observable<IEventoGenerico | undefined> =
    this.eventoSeleccionadoSubject.asObservable();

  // ─────────────────────────────────────────────────────────────────────────
  // NAVEGACIÓN PUNTUAL AL MAPA
  // ─────────────────────────────────────────────────────────────────────────

  // Centra el mapa una sola vez sin activar el seguimiento continuo.
  // Se usa al seleccionar un vehículo desde el panel de eventos.
  private panOnceSubject = new Subject<{
    coords: [number, number];
    radius?: number;
    zoom?: number;
  }>();
  public panOnce$ = this.panOnceSubject.asObservable();

  emitPanOnce(coords: [number, number], radius?: number, zoom?: number): void {
    this.panOnceSubject.next({ coords, radius, zoom });
  }

  // Pedido de dibujo del círculo de búsqueda desde el panel: el botón vive en
  // la barra lateral pero el dibujo ocurre sobre el mapa seleccionado.
  private dibujarCirculoSubject = new Subject<void>();
  public dibujarCirculo$ = this.dibujarCirculoSubject.asObservable();

  pedirDibujoCirculo(): void {
    this.dibujarCirculoSubject.next();
  }

  // Selección de vehículo pendiente de aplicar cuando lleguen los reportes.
  // Usado cuando se navega al mapa desde tratamiento de eventos.
  private pendingVehicleActivoIdSubject = new BehaviorSubject<string | null>(
    null,
  );
  public pendingVehicleActivoId$: Observable<string | null> =
    this.pendingVehicleActivoIdSubject.asObservable();

  // ─────────────────────────────────────────────────────────────────────────
  // ESTADO DEL SIDENAV Y LOADING
  // ─────────────────────────────────────────────────────────────────────────

  private sidenavOpenedSubject = new BehaviorSubject<boolean>(true);
  public sidenavOpened$: Observable<boolean> =
    this.sidenavOpenedSubject.asObservable();

  private sidenavHiddenSubject = new BehaviorSubject<boolean>(false);
  public sidenavHidden$: Observable<boolean> =
    this.sidenavHiddenSubject.asObservable();

  private loadingMapaSubject = new BehaviorSubject<boolean>(true);
  public loadingMapa$: Observable<boolean> =
    this.loadingMapaSubject.asObservable();

  // true mientras se recrean las instancias de mapa (cambio de cantMapas).
  // Deshabilita los botones de cantidad de mapas hasta que los vehículos estén cargados.
  private mapsRecreatingSubject = new BehaviorSubject<boolean>(false);
  public mapsRecreating$: Observable<boolean> =
    this.mapsRecreatingSubject.asObservable();

  // true mientras cualquier componente de búsqueda está procesando una consulta.
  // Deshabilita el cambio de tabs para que el usuario no pierda de vista qué buscó.
  private searchInProgressSubject = new BehaviorSubject<boolean>(false);
  public searchInProgress$: Observable<boolean> =
    this.searchInProgressSubject.asObservable();

  // ─────────────────────────────────────────────────────────────────────────
  // GETTERS SÍNCRONOS (acceso puntual sin suscripción)
  // ─────────────────────────────────────────────────────────────────────────

  get reportes(): IReporteBase<'Tracker 4G'>[] {
    return this.reportesSubject.value;
  }

  get ubicaciones(): IUbicacion[] {
    return this.ubicacionesSubject.value;
  }

  get recorridos(): IRecorrido[] {
    return this.recorridosSubject.value;
  }

  get recorridosDinamicos(): IRecorridoDinamico[] {
    return this.recorridosDinamicosSubject.value;
  }

  get configMarkers(): IConfigMarker[] {
    return this.configMarkersSubject.value;
  }

  get cantMapas(): number {
    return this.cantMapasSubject.value;
  }

  get mapaSeleccionado(): number {
    return this.mapaSeleccionadoSubject.value;
  }

  get tipoMapaBase(): TipoMapaBase {
    return this.tipoMapaBaseSubject.value;
  }

  get reporteSeleccionadoPorMapa(): {
    [mapa: number]: IReporteBase<'Tracker 4G'> | undefined;
  } {
    return this.reporteSeleccionadoPorMapaSubject.value;
  }

  get seleccionadosBarraLateral(): SeleccionadosBarraLateral[] {
    return this.seleccionadosBarraLateralSubject.value;
  }

  get filtrosPorMapa(): { [mapa: number]: MapInstanceFilter | undefined } {
    return this.filtrosPorMapaSubject.value;
  }

  get paradaSeleccionada(): Stop | undefined {
    return this.paradaSeleccionadaSubject.value;
  }

  get circuloBusqueda(): IGeoJSONCircle | undefined {
    return this.circuloBusquedaSubject.value;
  }

  get poligonoBusqueda(): IUbicacion | undefined {
    return this.poligonoBusquedaSubject.value;
  }

  get eventoSeleccionado(): IEventoGenerico | undefined {
    return this.eventoSeleccionadoSubject.value;
  }

  get sidenavOpened(): boolean {
    return this.sidenavOpenedSubject.value;
  }

  get sidenavHidden(): boolean {
    return this.sidenavHiddenSubject.value;
  }

  get loadingMapa(): boolean {
    return this.loadingMapaSubject.value;
  }

  get pendingVehicleActivoId(): string | null {
    return this.pendingVehicleActivoIdSubject.value;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // REPORTES
  // ─────────────────────────────────────────────────────────────────────────

  // Reemplaza la lista completa. Lo llama contenedor-mapa tras cada poll.
  setReportes(reportes: IReporteBase<'Tracker 4G'>[]): void {
    // El poll trae el reporte liviano. Para los vehículos con detalle cargado
    // (los seleccionados) se fusiona lo que ya había, así el card no pierde
    // dirección/odómetro/vehículo completo hasta el próximo pedido por id.
    if (this.idsConDetalle.size) {
      const previos = this.reportesSubject.value;
      for (let i = 0; i < reportes.length; i++) {
        const vehicleId =
          reportes[i].idEntidad || reportes[i].idsAsignados?.[0];
        if (!vehicleId || !this.idsConDetalle.has(vehicleId)) continue;
        const indexPrevio = this.reportesIndex.get(vehicleId);
        const previo =
          indexPrevio !== undefined ? previos[indexPrevio] : undefined;
        if (previo) reportes[i] = this.fusionarReportes(previo, reportes[i]);
      }
    }

    this.reportesSubject.next(reportes);
    this.rebuildReportesIndex();
  }

  // Reconstruye el índice vehicleId → posición (O(1) para updates WS).
  private rebuildReportesIndex(): void {
    const reportes = this.reportesSubject.value;
    const idx = new Map<string, number>();
    for (let i = 0; i < reportes.length; i++) {
      const r = reportes[i];
      if (r.idEntidad) idx.set(r.idEntidad, i);
      if (r.idsAsignados) {
        for (const a of r.idsAsignados) idx.set(a, i);
      }
    }
    this.reportesIndex = idx;
  }

  /**
   * Aplica un reporte WS al array (merge O(1) vía índice) y devuelve el merged,
   * o null si no corresponde (sin ubicación o vehículo no encontrado).
   * No emite — el caller decide (single vs batch).
   */
  private aplicarReporte(
    reporteNuevo: IReporteBase<'Tracker 4G'>,
  ): IReporteBase<'Tracker 4G'> | null {
    const vehicleId = reporteNuevo.idEntidad || reporteNuevo.idsAsignados?.[0];
    if (!vehicleId) return null;

    // Ignorar reportes SIN ubicación (status/eventos Tracker 4G sin geojson):
    // pisarían la última posición y el re-render descartaría el vehículo.
    const valores = reporteNuevo?.valores as any;
    const tieneUbicacion = !!(
      valores?.geojson?.coordinates?.length ||
      valores?.snappedGeojson?.coordinates?.length
    );
    if (!tieneUbicacion) return null;

    const reportes = this.reportesSubject.value;
    const index = this.reportesIndex.get(vehicleId);
    if (index === undefined || index < 0 || index >= reportes.length) {
      return null;
    }

    // MERGE: el payload WS es liviano (sin activo ni extras). Fusionar preserva
    // activo/identificación/grupos del polling y solo actualiza posición/estado.
    const existente = reportes[index];
    const merged = {
      ...existente,
      ...reporteNuevo,
      valores: {
        ...(existente?.valores as any),
        ...(reporteNuevo?.valores as any),
      },
    } as IReporteBase<'Tracker 4G'>;
    reportes[index] = merged;

    // Si el vehículo está seleccionado en algún mapa, refrescar el card en vivo.
    const activoId = merged.activo?._id;
    if (activoId) {
      const seleccionados = this.reporteSeleccionadoPorMapaSubject.value;
      let huboCambio = false;
      const actualizados = { ...seleccionados };
      for (const mapaKey of Object.keys(seleccionados)) {
        const mapaNum = +mapaKey;
        if (seleccionados[mapaNum]?.activo?._id === activoId) {
          actualizados[mapaNum] = merged;
          huboCambio = true;
        }
      }
      if (huboCambio) {
        this.reporteSeleccionadoPorMapaSubject.next(actualizados);
      }
    }

    return merged;
  }

  /**
   * Fusiona dos reportes del MISMO vehículo: `encima` pisa a `base` campo por
   * campo, descendiendo en los objetos que el listado liviano trae recortados
   * (valores, activo, activo.vehiculo, activo.grupo). Sin descender, el spread
   * plano del liviano borraría el detalle (valores.direccion, el vehiculo
   * completo, etc.) en vez de completarlo.
   */
  private fusionarReportes(
    base: IReporteBase<'Tracker 4G'>,
    encima: IReporteBase<'Tracker 4G'>,
  ): IReporteBase<'Tracker 4G'> {
    const activoBase = base?.activo as any;
    const activoEncima = encima?.activo as any;
    const activo =
      activoBase || activoEncima
        ? {
            ...activoBase,
            ...activoEncima,
            vehiculo:
              activoBase?.vehiculo || activoEncima?.vehiculo
                ? { ...activoBase?.vehiculo, ...activoEncima?.vehiculo }
                : undefined,
            grupo:
              activoBase?.grupo || activoEncima?.grupo
                ? { ...activoBase?.grupo, ...activoEncima?.grupo }
                : undefined,
          }
        : undefined;

    return {
      ...base,
      ...encima,
      valores: {
        ...(base?.valores as any),
        ...(encima?.valores as any),
      },
      ...(activo ? { activo } : {}),
    } as IReporteBase<'Tracker 4G'>;
  }

  /**
   * Aplica el reporte "detalle" (el pedido por id al seleccionar un vehículo)
   * sobre el liviano que ya está en la lista, y refresca los DOS espejos de
   * selección: reporteSeleccionadoPorMapa (de ahí come el card) y
   * seleccionadosBarraLateral (de ahí sale activo.vehiculo.idRecorrido, que
   * el mapa usa para dibujar el recorrido asignado).
   * Devuelve el reporte fusionado, o null si el vehículo no está en la lista.
   */
  aplicarDetalleReporte(
    detalle: IReporteBase<'Tracker 4G'>,
  ): IReporteBase<'Tracker 4G'> | null {
    const vehicleId = detalle?.idEntidad || detalle?.idsAsignados?.[0];
    if (!vehicleId) return null;

    const reportes = this.reportesSubject.value;
    const index = this.reportesIndex.get(vehicleId);
    if (index === undefined || index < 0 || index >= reportes.length) {
      return null;
    }

    const existente = reportes[index];
    const tsExistente =
      (existente?.valores as any)?.timestamp ?? existente?.fechaCreacion;
    const tsDetalle =
      (detalle?.valores as any)?.timestamp ?? detalle?.fechaCreacion;

    // Si mientras iba el request llegó una posición más nueva por WS, la del
    // liviano manda y el detalle solo aporta los campos que faltan.
    const merged =
      tsExistente && tsDetalle && tsDetalle < tsExistente
        ? this.fusionarReportes(detalle, existente)
        : this.fusionarReportes(existente, detalle);

    reportes[index] = merged;
    this.idsConDetalle.add(vehicleId);

    const activoId = merged.activo?._id;
    if (activoId) {
      const seleccionados = this.reporteSeleccionadoPorMapaSubject.value;
      let huboCambio = false;
      const actualizados = { ...seleccionados };
      for (const mapaKey of Object.keys(seleccionados)) {
        const mapaNum = +mapaKey;
        if (seleccionados[mapaNum]?.activo?._id === activoId) {
          actualizados[mapaNum] = merged;
          huboCambio = true;
        }
      }
      if (huboCambio) {
        this.reporteSeleccionadoPorMapaSubject.next(actualizados);
      }

      const barra = this.seleccionadosBarraLateralSubject.value;
      let cambioBarra = false;
      const barraActualizada = barra.map((s) => {
        if (s.reporteSeleccionado?.activo?._id !== activoId) return s;
        cambioBarra = true;
        return { ...s, reporteSeleccionado: merged };
      });
      if (cambioBarra) {
        this.seleccionadosBarraLateralSubject.next(barraActualizada);
      }
    }

    return merged;
  }

  // El vehículo se deseleccionó: su reporte vuelve a ser el liviano del listado.
  olvidarDetalleReporte(vehicleId?: string): void {
    if (vehicleId) this.idsConDetalle.delete(vehicleId);
  }

  // Actualiza UN reporte (path per-vehículo: seleccionado/seguido, payload completo).
  updateSingleReporte(reporteNuevo: IReporteBase<'Tracker 4G'>): void {
    const merged = this.aplicarReporte(reporteNuevo);
    if (merged) this.reporteRealTimeSubject.next(merged);
  }

  // Actualiza un LOTE de reportes (batch WS del mapa). Hace el merge O(1) de cada
  // uno y emite UNA sola vez el array de cambiados → mínimo overhead de rxjs.
  updateReportesBatch(reportesNuevos: IReporteBase<'Tracker 4G'>[]): void {
    if (!reportesNuevos?.length) return;
    const cambiados: IReporteBase<'Tracker 4G'>[] = [];
    for (const reporteNuevo of reportesNuevos) {
      const merged = this.aplicarReporte(reporteNuevo);
      if (merged) cambiados.push(merged);
    }
    if (cambiados.length) {
      this.reporteRealTimeBatchSubject.next(cambiados);
    }
  }

  refrescarReportes(): void {
    this.refrescarReportesSubject.next();
  }

  setUbicaciones(ubicaciones: IUbicacion[]): void {
    this.ubicacionesSubject.next(ubicaciones);
  }

  setRecorridos(recorridos: IRecorrido[]): void {
    this.recorridosSubject.next(recorridos);
  }

  setRecorridosDinamicos(recorridosDinamicos: IRecorridoDinamico[]): void {
    this.recorridosDinamicosSubject.next(recorridosDinamicos);
  }

  setConfigMarkers(configMarkers: IConfigMarker[]): void {
    this.configMarkersSubject.next(configMarkers);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // CONFIGURACIÓN DE MAPAS
  // ─────────────────────────────────────────────────────────────────────────

  // Cuando se reduce la cantidad de mapas, limpia el estado de los eliminados
  // y borra reporteSeleccionado de los que quedan para evitar race conditions
  // al recrear las instancias del mapa (applySearchStateToInstance es async).
  setCantMapas(cant: number): void {
    const prev = this.cantMapasSubject.value;
    this.cantMapasSubject.next(cant);

    if (cant >= prev) return;

    // seleccionadosBarraLateral: eliminar entradas de mapas fuera de rango
    // y limpiar reporteSeleccionado de los restantes
    const seleccionados = this.seleccionadosBarraLateralSubject.value
      .filter((s) => s.mapaSeleccionado <= cant)
      .map((s) => ({
        ...s,
        reporteSeleccionado:
          undefined as SeleccionadosBarraLateral['reporteSeleccionado'],
      }));
    this.seleccionadosBarraLateralSubject.next(seleccionados);

    // Limpiar los subjects espejo para los mapas eliminados
    const filtros = { ...this.filtrosPorMapaSubject.value };
    const reportesPorMapa = { ...this.reporteSeleccionadoPorMapaSubject.value };
    const vehiculosFiltrados = new Map(
      this.vehiculosFiltradosPorMapaSubject.value,
    );

    for (let i = cant + 1; i <= prev; i++) {
      delete filtros[i];
      delete reportesPorMapa[i];
      vehiculosFiltrados.delete(i);
    }

    this.filtrosPorMapaSubject.next(filtros);
    this.reporteSeleccionadoPorMapaSubject.next(reportesPorMapa);
    this.vehiculosFiltradosPorMapaSubject.next(vehiculosFiltrados);
  }

  setMapaSeleccionado(mapa: number): void {
    this.mapaSeleccionadoSubject.next(mapa);
  }

  setTipoMapaBase(tipo: TipoMapaBase): void {
    this.tipoMapaBaseSubject.next(tipo);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ESTADO DE BARRA LATERAL
  // ─────────────────────────────────────────────────────────────────────────

  // Reemplaza todo el array (usado al restaurar desde sessionStorage).
  setSeleccionadosBarraLateral(
    seleccionados: SeleccionadosBarraLateral[],
  ): void {
    this.seleccionadosBarraLateralSubject.next([...seleccionados]);
  }

  // Actualiza campos del mapa activo. Lo usan los componentes de búsqueda
  // para guardar parámetros y resultados sin conocer el número de mapa.
  updateSeleccionadoBarraLateral(
    changes: Partial<Omit<SeleccionadosBarraLateral, 'mapaSeleccionado'>>,
  ): void {
    const mapaActual = this.mapaSeleccionado;
    const current = [...this.seleccionadosBarraLateralSubject.value];
    const index = current.findIndex((s) => s.mapaSeleccionado === mapaActual);

    if (index >= 0) {
      current[index] = { ...current[index], ...changes };
    } else {
      current.push({ mapaSeleccionado: mapaActual, ...changes });
    }

    this.seleccionadosBarraLateralSubject.next(current);
  }

  // Actualiza campos de un mapa específico por número.
  // Lo usa contenedor-mapa al recibir el click en un vehículo del mapa
  // (el evento sube con el número de mapa incluido).
  updateSeleccionadoBarraLateralPorNumero(
    mapaNum: number,
    changes: Partial<SeleccionadosBarraLateral>,
  ): void {
    const current = [...this.seleccionadosBarraLateralSubject.value];
    const index = current.findIndex((s) => s.mapaSeleccionado === mapaNum);

    if (index >= 0) {
      current[index] = { ...current[index], ...changes };
    } else {
      current.push({ mapaSeleccionado: mapaNum, ...changes });
    }

    this.seleccionadosBarraLateralSubject.next(current);
  }

  // Devuelve el estado del mapa activo (acceso síncrono).
  getSeleccionadoActual(): SeleccionadosBarraLateral | undefined {
    return this.seleccionadosBarraLateral.find(
      (s) => s.mapaSeleccionado === this.mapaSeleccionado,
    );
  }

  // Devuelve el estado de un mapa específico (acceso síncrono).
  getSeleccionadoPorNumero(
    mapaNum: number,
  ): SeleccionadosBarraLateral | undefined {
    return this.seleccionadosBarraLateral.find(
      (s) => s.mapaSeleccionado === mapaNum,
    );
  }

  // Actualiza el espejo reporteSeleccionadoPorMapa (en paralelo a seleccionadosBarraLateral).
  setReporteSeleccionadoPorMapa(
    mapa: number,
    reporte: IReporteBase<'Tracker 4G'> | undefined,
  ): void {
    const current = { ...this.reporteSeleccionadoPorMapaSubject.value };
    current[mapa] = reporte;
    this.reporteSeleccionadoPorMapaSubject.next(current);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FILTROS
  // ─────────────────────────────────────────────────────────────────────────

  setFiltrosPorMapa(
    mapa: number,
    filtros: MapInstanceFilter | undefined,
  ): void {
    const current = { ...this.filtrosPorMapaSubject.value };
    current[mapa] = filtros;
    this.filtrosPorMapaSubject.next(current);
  }

  setFiltrosActual(filtros: MapInstanceFilter | undefined): void {
    this.setFiltrosPorMapa(this.mapaSeleccionado, filtros);
  }

  getFiltrosActual(): MapInstanceFilter | undefined {
    return this.filtrosPorMapa[this.mapaSeleccionado];
  }

  // IDs de vehículos visibles para un mapa (resultado de una búsqueda activa).
  setVehiculosFiltradosPorMapa(
    mapIndex: number,
    reportes: IReporteBase<'Tracker 4G'>[],
  ): void {
    const current = new Map(this.vehiculosFiltradosPorMapaSubject.value);
    const vehicleIds = new Set(
      reportes
        .map((r) => r.idEntidad || r.idsAsignados?.[0])
        .filter(Boolean) as string[],
    );
    current.set(mapIndex, vehicleIds);
    this.vehiculosFiltradosPorMapaSubject.next(current);
  }

  // Elimina el filtro de búsqueda activa → vuelven a verse todos los vehículos.
  clearVehiculosFiltradosPorMapa(mapIndex: number): void {
    const current = new Map(this.vehiculosFiltradosPorMapaSubject.value);
    current.delete(mapIndex);
    this.vehiculosFiltradosPorMapaSubject.next(current);
  }

  getVehiculosFiltradosPorMapa(mapIndex: number): Set<string> {
    return (
      this.vehiculosFiltradosPorMapaSubject.value.get(mapIndex) ?? new Set()
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // ELEMENTOS INTERACTIVOS
  // ─────────────────────────────────────────────────────────────────────────

  setParadaSeleccionada(parada: Stop | undefined): void {
    this.paradaSeleccionadaSubject.next(parada);
  }

  setCirculoBusqueda(circulo: IGeoJSONCircle | undefined): void {
    this.circuloBusquedaSubject.next(circulo);
  }

  setPoligonoBusqueda(ubicacion: IUbicacion | undefined): void {
    this.poligonoBusquedaSubject.next(ubicacion);
  }

  setEventoSeleccionado(evento: IEventoGenerico | undefined): void {
    this.eventoSeleccionadoSubject.next(evento);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // SIDENAV, LOADING Y SELECCIÓN PENDIENTE
  // ─────────────────────────────────────────────────────────────────────────

  setSidenavOpened(opened: boolean): void {
    this.sidenavOpenedSubject.next(opened);
  }

  setSidenavHidden(hidden: boolean): void {
    this.sidenavHiddenSubject.next(hidden);
  }

  setLoadingMapa(loading: boolean): void {
    this.loadingMapaSubject.next(loading);
  }

  setMapsRecreating(recreating: boolean): void {
    this.mapsRecreatingSubject.next(recreating);
  }

  setSearchInProgress(inProgress: boolean): void {
    this.searchInProgressSubject.next(inProgress);
  }

  // Vehículo que debe seleccionarse en cuanto lleguen los reportes.
  setPendingVehicleActivoId(activoId: string | null): void {
    this.pendingVehicleActivoIdSubject.next(activoId);
  }

  clearPendingVehicleActivoId(): void {
    this.pendingVehicleActivoIdSubject.next(null);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RESET
  // ─────────────────────────────────────────────────────────────────────────

  reset(): void {
    this.reportesSubject.next([]);
    this.ubicacionesSubject.next([]);
    this.recorridosSubject.next([]);
    this.cantMapasSubject.next(1);
    this.mapaSeleccionadoSubject.next(1);
    this.tipoMapaBaseSubject.next('osm');
    this.reporteSeleccionadoPorMapaSubject.next({});
    this.seleccionadosBarraLateralSubject.next([]);
    this.filtrosPorMapaSubject.next({});
    this.paradaSeleccionadaSubject.next(undefined);
    this.circuloBusquedaSubject.next(undefined);
    this.sidenavOpenedSubject.next(true);
    this.sidenavHiddenSubject.next(false);
    this.loadingMapaSubject.next(true);
    this.pendingVehicleActivoIdSubject.next(null);
  }
}
